import type { ComponentType, ReactNode } from "react";

/**
 * mode="web"   → identity selector shows {account when session, email, anonymous}.
 *                NO login UI is rendered, ever. account option simply does not
 *                appear when the user is signed-out.
 * mode="portal" → identity is forced to "account". Selector hidden. Widget
 *                 should only mount inside _authenticated; if session is
 *                 missing it falls back to a single "Sign in to send feedback"
 *                 link with `loginUrl`.
 */
export type FeedbackMode = "web" | "portal";

export type FeedbackIdentity = "account" | "email" | "anonymous";

/**
 * Minimal session shape the widget needs. Both apps/web and apps/app session
 * users carry these fields (apps/app's SessionUser only carries userId, but
 * portal mode displays only the email — host can layer in a richer profile).
 */
export interface FeedbackSessionUser {
  userId?: string;
  /** Some hosts call it `id` (legacy). Read both. */
  id?: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  [extra: string]: unknown;
}

/**
 * Server payload — matches POST /api/feedback in apps/api/src/routes/feedback.ts.
 * Both `userId === null` (anonymous) and `userId === <id>` (signed-in) are
 * accepted by the backend.
 */
export interface FeedbackPayload {
  type: string;
  description: string;
  userId: string | null;
  userEmail: string | null;
  userType: "anonymous" | "user";
  pageUrl: string | null;
  pageTitle: string | null;
  locale: string | null;
  device: { userAgent: string; screenSize: string } | null;
  consoleErrors: CapturedConsoleError[];
  networkErrors: CapturedNetworkError[];
  performance: PerformanceMetrics | null;
  screenshots: string[];
}

export interface CapturedConsoleError {
  message: string;
  source?: string;
  line?: number;
  col?: number;
  timestamp: number;
}

export interface CapturedNetworkError {
  url: string;
  method: string;
  status: number | null;
  statusText?: string;
  timestamp: number;
}

export interface PerformanceMetrics {
  pageLoadMs: number;
  domContentLoadedMs: number;
  ttfbMs: number;
  memoryMB?: number;
}

/**
 * Translation strings the widget needs. Host supplies a fully-resolved
 * messages object — keeps the package framework-agnostic (next-intl on web,
 * Lingui on portal). All strings except `types` are plain. `types` is keyed
 * by the same identifiers consumed by `feedbackTypeKeys` below.
 */
export interface FeedbackMessages {
  button: string;
  title: string;
  close: string;
  typeQuestion: string;
  types: {
    bug: string;
    idea: string;
    question: string;
    praise: string;
    complaint: string;
    incorrect_data: string;
  };
  descriptionPlaceholder: string;
  sendAs: string;
  emailPlaceholder: string;
  identity: {
    account: string;
    email: string;
    anonymous: string;
    user: string;
    accountNote: string;
  };
  screenshotToggle: string;
  capturingScreenshot: string;
  screenshotAttached: string;
  annotateScreenshot: string;
  uploadImages: string;
  maxUploadsReached: string;
  invalidImageType: string;
  imageTooLarge: string;
  submit: string;
  submitting: string;
  thankYou: string;
  submitted: string;
  /** Shown above the submit button when sending failed (network, 5xx, …). */
  submitFailed: string;
  /** Shown on HTTP 429 — too many reports in a short time. */
  rateLimited: string;
  /** Shown on HTTP 401 — "send from my account" with an expired session. */
  sessionExpired: string;
  /** Portal mode only — shown when session somehow missing. */
  signInToSendFeedback?: string;
  /** Portal mode only — small badge under header showing "Sending as <email>". */
  sendingAs?: string;
}

export const feedbackTypeKeys = [
  "question",
  "idea",
  "praise",
  "bug",
  "incorrect_data",
  "complaint",
] as const;

export type FeedbackTypeKey = (typeof feedbackTypeKeys)[number];

/**
 * Minimal apiFetch shape the widget calls. Matches both
 * apps/web's local helper and `@pontimed/api-client.apiFetch`.
 */
export interface FeedbackApiFetch {
  post<T = unknown>(path: string, body?: unknown, init?: unknown): Promise<T>;
}

/**
 * Minimal ResponsiveSheet contract — apps/web has its own, apps/app has its
 * own. Both expose roughly the same prop surface; the widget passes only the
 * subset it actually needs. Typed loose with `any` because each host has its
 * own richer signature (e.g. apps/web supports headerSlot/footer/width that
 * we never use here) — keeping the contract narrow but the type compat-loose
 * avoids the host needing wrapper components.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FeedbackSheetComponent = ComponentType<any>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FeedbackButtonComponent = ComponentType<any>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FeedbackInputComponent = ComponentType<any>;

/**
 * Documented prop shape the widget actually passes — host implementations
 * must accept at least these (apps/web ResponsiveSheet, apps/app
 * ResponsiveSheet). Used only for inline doc; not enforced at the type
 * level so hosts with strict signatures still fit.
 */
export interface FeedbackSheetComponentProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  ariaLabel?: string;
  closeLabel?: string;
  desktop?: "dialog" | "float-right";
  panelClassName?: string;
  children?: ReactNode;
}

/**
 * Host-supplied components — all required so the widget renders identically
 * to whatever idiom the host uses (apps/web shadcn-lite vs apps/app base-ui).
 */
export interface FeedbackComponents {
  ResponsiveSheet: FeedbackSheetComponent;
  Button: FeedbackButtonComponent;
  Input: FeedbackInputComponent;
}

/** Class-merge helper. Pass `cn` from `@pontimed/ui`. Loose any[] to fit
 *  clsx-style variadic ClassValue arguments without forcing the host to
 *  re-declare the type. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ClassMerge = (...inputs: any[]) => string;
